import type {
  Maybe,
  SanityGlobalProductVariant,
  SanityProductMediaImage,
  SanityProductExternalVideo,
  SanityProductVideo,
} from '@repo/models/gatsby-graphql-types';

import { getCanonicalUrl, languageMapping } from './getCanonicalUrl';
import removeOptionColorValue from './removeOptionColorValue';
import type {
  CustomShopifyProduct,
  GetProductSchemaOptions,
  ProductSchemaBreadcrumbItem,
  ProductSchemaFaqItem,
  ProductSchemaVideoItem,
} from './getProductSchema.types';

export type {
  CustomShopifyProduct,
  ProductSchemaAggregateRating,
  ProductSchemaReview,
  ProductSchemaBreadcrumbItem,
  ProductSchemaFaqItem,
  ProductSchemaVideoItem,
  GetProductSchemaOptions,
} from './getProductSchema.types';

type SanityMediaItem =
  SanityProductExternalVideo | SanityProductMediaImage | SanityProductVideo;

const schemaPropertyMap = {
  size: 'https://schema.org/size',
  color: 'https://schema.org/color',
  colour: 'https://schema.org/color',
  material: 'https://schema.org/material',
  pattern: 'https://schema.org/pattern',
} as const;

type SchemaPropertyField = 'color' | 'size' | 'material' | 'pattern';

const colorKeys = new Set([
  'color',
  'colour',
  'farbe',
  'couleur',
  'colore',
  'kleur',
]);

const isProductMediaImage = (
  item: Maybe<SanityMediaItem>
): item is SanityProductMediaImage =>
  item !== null &&
  item !== undefined &&
  (item as SanityProductMediaImage).image !== undefined;

const getLegacyColor = (
  variant: Maybe<SanityGlobalProductVariant> | undefined
): string | undefined => {
  const colorOption = variant?.selectedOptions?.find(option =>
    colorKeys.has(option?.name?.toLowerCase().trim() || '')
  );
  const rawColorValue = colorOption?.value;
  if (!rawColorValue || rawColorValue === 'Default Title') {
    return undefined;
  }
  return rawColorValue;
};

/**
 * Color used to share media across size siblings of the same colorway.
 * Prefer selectedOptions (what PetSafe and most sites query); fall back to
 * Shopify option1/2/3 when those fields are present in the product payload.
 */
const getVariantColorValue = (
  variant: Maybe<SanityGlobalProductVariant> | undefined
): string | undefined => {
  const fromSelectedOptions = getLegacyColor(variant);
  if (fromSelectedOptions) return fromSelectedOptions;

  return variant?.option1 || variant?.option2 || variant?.option3 || undefined;
};

const getProductImage = (
  product: CustomShopifyProduct,
  selectedVariant: Maybe<SanityGlobalProductVariant> | undefined
): string | undefined => {
  const allVariants = product.variants || [];
  const allMedia = (product.media || []).filter(isProductMediaImage);

  const variantMediaIds = new Set(
    allVariants.flatMap(v => v?.media?.map(m => m?.id) || [])
  );

  const sharedMedia = allMedia.find(item => !variantMediaIds.has(item?.id));

  const variantImage = selectedVariant?.media?.[0]?.image?.src;
  if (variantImage) return variantImage;

  const colorValue = getVariantColorValue(selectedVariant);
  if (colorValue) {
    const variantWithSameColor = allVariants.find(variant => {
      const variantColor = getVariantColorValue(variant);
      return variantColor === colorValue && variant?.media?.length;
    });

    const sameColorImage = variantWithSameColor?.media?.[0]?.image?.src;
    if (sameColorImage) return sameColorImage;
  }

  const sharedImage = sharedMedia?.image?.src;
  if (sharedImage) return sharedImage;

  return allMedia[0]?.image?.src;
};

type GtinFields = Partial<{
  gtin: string;
  gtin8: string;
  gtin12: string;
  gtin13: string;
  gtin14: string;
  hasGS1DigitalLink: string;
}>;

const isGs1DigitalLink = (value: string) =>
  /^https?:\/\/(?:www\.)?id\.gs1\.org\//i.test(value);

const toDigits = (value: string) => value.replaceAll(/\D/g, '');

const getGtinFields = (value?: string): GtinFields => {
  if (!value) return {};

  if (isGs1DigitalLink(value)) {
    return { hasGS1DigitalLink: value };
  }

  const digits = toDigits(value);
  if (!digits) return {};

  switch (digits.length) {
    case 8:
      return { gtin8: digits, gtin: digits };
    case 12:
      return { gtin12: digits, gtin: digits };
    case 13:
      return { gtin13: digits, gtin: digits };
    case 14:
      return { gtin14: digits, gtin: digits };
    default:
      return {};
  }
};

const getSchemaPropertyUrl = (optionName: string): string | undefined => {
  const lower = optionName.toLowerCase().trim();
  if (lower in schemaPropertyMap) {
    return schemaPropertyMap[lower as keyof typeof schemaPropertyMap];
  }
  if (colorKeys.has(lower)) {
    return schemaPropertyMap.color;
  }
  return undefined;
};

const getVariantOptionProperties = (
  variant: Maybe<SanityGlobalProductVariant> | undefined
): Partial<Record<SchemaPropertyField, string>> => {
  const properties: Partial<Record<SchemaPropertyField, string>> = {};

  variant?.selectedOptions?.forEach(option => {
    const optionName = option?.name?.toLowerCase().trim() || '';
    const value = option?.value;
    if (!value || value === 'Default Title') return;

    if (optionName in schemaPropertyMap) {
      const field =
        optionName === 'colour' ? 'color' : (optionName as SchemaPropertyField);
      properties[field] = value;
      return;
    }

    if (colorKeys.has(optionName)) {
      properties.color = value;
    }
  });

  return properties;
};

const getVariantProductName = (
  productTitle: string | undefined,
  variant: Maybe<SanityGlobalProductVariant> | undefined
): string | undefined => {
  if (!productTitle) return undefined;

  const optionValues =
    variant?.selectedOptions
      ?.map(option => option?.value?.trim())
      .filter((value): value is string => !!value && value !== 'Default Title')
      .map(value => removeOptionColorValue(value).trim())
      .filter(Boolean) ?? [];

  if (optionValues.length > 0) {
    return `${productTitle} - ${optionValues.join(' / ')}`;
  }

  const rawVariantTitle = variant?.title?.trim();
  if (rawVariantTitle && rawVariantTitle !== 'Default Title') {
    const cleanedVariantTitle = removeOptionColorValue(rawVariantTitle).trim();
    if (cleanedVariantTitle) {
      return `${productTitle} - ${cleanedVariantTitle}`;
    }
  }

  return productTitle;
};

const collectVariesBy = (
  variants: Maybe<SanityGlobalProductVariant>[]
): string[] => {
  const variesBySet = new Set<string>();

  variants.forEach(variant => {
    variant?.selectedOptions?.forEach(option => {
      const schemaUrl = getSchemaPropertyUrl(option?.name || '');
      if (schemaUrl) {
        variesBySet.add(schemaUrl);
      }
    });
  });

  return Array.from(variesBySet);
};

const stripShopifyProductGid = (
  shopifyProduct: CustomShopifyProduct
): string => {
  const id = shopifyProduct.shopifyId;
  if (id) {
    return id.replace('gid://shopify/Product/', '');
  }
  return shopifyProduct.handle ?? '';
};

const getRoutedProductSlug = (
  shopifyProduct: CustomShopifyProduct,
  options: GetProductSchemaOptions
): string | undefined => options.productSlug || shopifyProduct.handle;

const getProductGroupBaseUrl = (
  siteUrl: string,
  locale: string,
  handle: string,
  isNonCommerce: boolean
): string => {
  const lang = languageMapping[locale.toLowerCase()] ?? '';
  const langPrefix = lang ? `/${lang}` : '';
  const suffix = siteUrl.includes('petsafe.com') ? '/' : '';
  const pathPrefix = isNonCommerce ? 'i' : 'p';
  return `${siteUrl}${langPrefix}/${pathPrefix}/${handle}${suffix}`;
};

const buildDefaultVariantUrl = (
  shopifyProduct: CustomShopifyProduct,
  variantSku: string,
  options: GetProductSchemaOptions
): string | undefined => {
  if (!options.siteUrl || !options.locale) {
    return undefined;
  }

  const routedSlug = getRoutedProductSlug(shopifyProduct, options);
  if (!routedSlug) {
    return undefined;
  }

  // Intentionally omit SEOMetadata: product-level canonicalUrl overrides must not
  // collapse every variant onto one URL. Variant @id/url identity is always the
  // default PDP route so the same SKU stays stable across sibling pages.
  return getCanonicalUrl(
    options.siteUrl,
    options.locale,
    { slug: { current: routedSlug } },
    {},
    options.isNonCommerce ?? false,
    variantSku
  );
};

const resolveVariantCanonicalUrl = (
  shopifyProduct: CustomShopifyProduct,
  variantSku: string | undefined,
  activeSku: string,
  options: GetProductSchemaOptions,
  pageCanonicalUrl: string
): string | undefined => {
  if (!variantSku) return undefined;

  const defaultVariantUrl = buildDefaultVariantUrl(
    shopifyProduct,
    variantSku,
    options
  );
  if (defaultVariantUrl) {
    return defaultVariantUrl;
  }

  const pageSku = options.pageSku ?? activeSku;
  if (variantSku === pageSku) {
    return pageCanonicalUrl;
  }

  return undefined;
};

const buildOffer = (
  variant: Maybe<SanityGlobalProductVariant> | undefined,
  priceCurrency: string,
  offerUrl?: string
) => {
  const refurbished = false;

  return {
    '@type': 'Offer',
    ...(offerUrl ? { url: offerUrl } : {}),
    priceCurrency,
    price: variant?.price?.toString(),
    itemCondition: refurbished
      ? 'https://schema.org/RefurbishedCondition'
      : 'https://schema.org/NewCondition',
    availability: variant?.availableForSale
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock',
  };
};

const buildVariantProduct = (
  shopifyProduct: CustomShopifyProduct,
  variant: Maybe<SanityGlobalProductVariant> | undefined,
  priceCurrency: string,
  activeSku: string,
  options: GetProductSchemaOptions,
  pageCanonicalUrl: string,
  includeOptionProperties: boolean
) => {
  const description =
    shopifyProduct?.description || shopifyProduct.schemaDescription;
  const variantCanonicalUrl = resolveVariantCanonicalUrl(
    shopifyProduct,
    variant?.sku,
    activeSku,
    options,
    pageCanonicalUrl
  );
  const rawBarcode = variant?.barcode?.trim();
  const gtinFields = getGtinFields(rawBarcode);
  const optionProperties = includeOptionProperties
    ? getVariantOptionProperties(variant)
    : {};
  const color = includeOptionProperties
    ? optionProperties.color
    : getLegacyColor(variant);

  return {
    '@type': 'Product',
    ...(variantCanonicalUrl
      ? {
          '@id': `${variantCanonicalUrl}#product`,
          url: variantCanonicalUrl,
        }
      : {}),
    name: getVariantProductName(shopifyProduct.title, variant),
    image: getProductImage(shopifyProduct, variant),
    description,
    sku: variant?.sku,
    ...(color ? { color } : {}),
    ...(includeOptionProperties && optionProperties.size
      ? { size: optionProperties.size }
      : {}),
    ...(includeOptionProperties && optionProperties.material
      ? { material: optionProperties.material }
      : {}),
    ...(includeOptionProperties && optionProperties.pattern
      ? { pattern: optionProperties.pattern }
      : {}),
    ...gtinFields,
    brand: {
      '@type': 'Brand',
      name: shopifyProduct?.vendor,
    },
    offers: buildOffer(variant, priceCurrency, variantCanonicalUrl),
  };
};

const buildReviewSchemaFields = (
  options?: GetProductSchemaOptions
): Record<string, unknown> => {
  const aggregateRating = options?.aggregateRating;
  if (
    !aggregateRating ||
    aggregateRating.reviewCount <= 0 ||
    aggregateRating.ratingValue == null
  ) {
    return {};
  }

  const fields: Record<string, unknown> = {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: aggregateRating.ratingValue,
      reviewCount: aggregateRating.reviewCount,
      bestRating: aggregateRating.bestRating ?? 5,
      worstRating: aggregateRating.worstRating ?? 1,
    },
  };

  const reviews = options?.reviews;
  if (!reviews?.length) {
    return fields;
  }

  fields.review = reviews.map(review => ({
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: review.authorName,
    },
    datePublished: review.datePublished,
    reviewBody: review.reviewBody,
    ...(review.name ? { name: review.name } : {}),
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.ratingValue,
      bestRating: review.bestRating ?? 5,
      worstRating: review.worstRating ?? 1,
    },
  }));

  return fields;
};

// Variant-scoped BV data belongs only to the routed SKU. When lifecycle
// filtering drops that variant and this path falls back to another Product,
// attaching the routed reviews would publish SKU B's ratings on SKU A.
const shouldAttachReviewFields = (
  options: GetProductSchemaOptions | undefined,
  selectedSku?: string | null
): boolean => {
  if (options?.reviewsAreProductLevel) return true;

  const pageSku = options?.pageSku ?? selectedSku;
  if (!pageSku) return true;

  return pageSku === selectedSku;
};

const buildLegacyProduct = (
  shopifyProduct: CustomShopifyProduct,
  selectedVariant: Maybe<SanityGlobalProductVariant> | undefined,
  priceCurrency: string
) => {
  const color = getLegacyColor(selectedVariant);
  const rawBarcode = selectedVariant?.barcode?.trim();
  const gtinFields = getGtinFields(rawBarcode);

  return {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    '@id': globalThis.window === undefined ? '' : globalThis.location.href,
    name: shopifyProduct.title,
    image: getProductImage(shopifyProduct, selectedVariant),
    description:
      shopifyProduct?.description || shopifyProduct.schemaDescription,
    sku: selectedVariant?.sku,
    ...(color ? { color } : {}),
    ...gtinFields,
    brand: {
      '@type': 'Brand',
      name: shopifyProduct?.vendor,
    },
    offers: buildOffer(selectedVariant, priceCurrency),
  };
};

const buildGraphProduct = (
  shopifyProduct: CustomShopifyProduct,
  selectedVariant: Maybe<SanityGlobalProductVariant> | undefined,
  priceCurrency: string,
  canonicalUrl: string,
  options?: GetProductSchemaOptions
) => {
  const color = getLegacyColor(selectedVariant);
  const rawBarcode = selectedVariant?.barcode?.trim();
  const gtinFields = getGtinFields(rawBarcode);

  return {
    '@type': 'Product',
    '@id': `${canonicalUrl}#product`,
    url: canonicalUrl,
    mainEntityOfPage: canonicalUrl,
    name: shopifyProduct.title,
    image: getProductImage(shopifyProduct, selectedVariant),
    description:
      shopifyProduct?.description || shopifyProduct.schemaDescription,
    sku: selectedVariant?.sku,
    ...(color ? { color } : {}),
    ...gtinFields,
    brand: {
      '@type': 'Brand',
      name: shopifyProduct?.vendor,
    },
    offers: buildOffer(selectedVariant, priceCurrency, canonicalUrl),
    ...(shouldAttachReviewFields(options, selectedVariant?.sku)
      ? buildReviewSchemaFields(options)
      : {}),
  };
};

const buildBreadcrumbListSchema = (
  items: ReadonlyArray<ProductSchemaBreadcrumbItem>,
  canonicalUrl: string
) => {
  if (items.length < 2) return null;

  return {
    '@type': 'BreadcrumbList',
    '@id': `${canonicalUrl}#breadcrumb`,
    itemListElement: items.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      ...(crumb.item ? { item: crumb.item } : {}),
    })),
  };
};

const buildFaqPageSchema = (
  faqs: ReadonlyArray<ProductSchemaFaqItem>,
  canonicalUrl: string
) => {
  if (!faqs.length) return null;

  return {
    '@type': 'FAQPage',
    '@id': `${canonicalUrl}#faq`,
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

const buildVideoObjectSchemas = (
  videos: ReadonlyArray<ProductSchemaVideoItem>,
  canonicalUrl: string
) => {
  const nodes: Record<string, unknown>[] = [];

  videos.forEach((video, index) => {
    if (!video.name) return;
    if (!video.contentUrl && !video.embedUrl) return;
    if (!video.uploadDate) return;
    if (!video.thumbnailUrl) return;

    nodes.push({
      '@type': 'VideoObject',
      '@id': `${canonicalUrl}#video-${index}`,
      name: video.name,
      uploadDate: video.uploadDate,
      thumbnailUrl: video.thumbnailUrl,
      ...(video.description ? { description: video.description } : {}),
      ...(video.contentUrl ? { contentUrl: video.contentUrl } : {}),
      ...(video.embedUrl ? { embedUrl: video.embedUrl } : {}),
    });
  });

  return nodes;
};

const buildRichContentGraphNodes = (
  options: GetProductSchemaOptions,
  canonicalUrl: string
): unknown[] => {
  const nodes: unknown[] = [];

  const breadcrumbNode = options.breadcrumbs
    ? buildBreadcrumbListSchema(options.breadcrumbs, canonicalUrl)
    : null;
  if (breadcrumbNode) nodes.push(breadcrumbNode);

  const faqNode = options.faqs
    ? buildFaqPageSchema(options.faqs, canonicalUrl)
    : null;
  if (faqNode) nodes.push(faqNode);

  if (options.videos?.length) {
    nodes.push(...buildVideoObjectSchemas(options.videos, canonicalUrl));
  }

  return nodes;
};

export const getRichContentSchema = (
  options: GetProductSchemaOptions,
  canonicalUrl?: string
) => {
  if (!canonicalUrl) return null;

  const nodes = buildRichContentGraphNodes(options, canonicalUrl);
  if (!nodes.length) return null;

  return {
    '@context': 'https://schema.org',
    '@graph': nodes,
  };
};

const buildProductGroup = (
  shopifyProduct: CustomShopifyProduct,
  variants: Maybe<SanityGlobalProductVariant>[],
  selectedVariant: Maybe<SanityGlobalProductVariant> | undefined,
  priceCurrency: string,
  activeSku: string,
  options: GetProductSchemaOptions,
  pageCanonicalUrl: string
) => {
  const description =
    shopifyProduct?.description || shopifyProduct.schemaDescription;
  const variesBy = collectVariesBy(variants);
  const isNonCommerce = options.isNonCommerce ?? false;

  let productGroupId = `${pageCanonicalUrl}#productgroup`;
  const routedSlug = getRoutedProductSlug(shopifyProduct, options);
  if (options.siteUrl && options.locale && routedSlug) {
    const productGroupBaseUrl = getProductGroupBaseUrl(
      options.siteUrl,
      options.locale,
      routedSlug,
      isNonCommerce
    );
    productGroupId = `${productGroupBaseUrl}#productgroup`;
  }

  const reviewFields = buildReviewSchemaFields(options);
  const attachReviewsToProductGroup = Boolean(options.reviewsAreProductLevel);
  const pageSku = options.pageSku ?? activeSku;

  return {
    '@type': 'ProductGroup',
    '@id': productGroupId,
    name: shopifyProduct.title,
    description,
    brand: {
      '@type': 'Brand',
      name: shopifyProduct?.vendor,
    },
    productGroupID: stripShopifyProductGid(shopifyProduct),
    image: getProductImage(shopifyProduct, selectedVariant),
    ...(variesBy.length > 0 ? { variesBy } : {}),
    hasVariant: variants.map(variant => {
      const variantProduct = buildVariantProduct(
        shopifyProduct,
        variant,
        priceCurrency,
        activeSku,
        options,
        pageCanonicalUrl,
        true
      );

      if (
        !attachReviewsToProductGroup &&
        Object.keys(reviewFields).length > 0 &&
        variant?.sku === pageSku
      ) {
        return {
          ...variantProduct,
          ...reviewFields,
        };
      }

      return variantProduct;
    }),
    ...(attachReviewsToProductGroup ? reviewFields : {}),
  };
};

const getProductSchema = (
  shopifyProduct: CustomShopifyProduct,
  activeSku: string,
  options?: GetProductSchemaOptions
) => {
  if (!shopifyProduct) return null;

  const variants = shopifyProduct.variants || [];
  const selectedVariant =
    variants.find(variant => variant?.sku === activeSku) || variants[0];

  const priceCurrency =
    shopifyProduct.priceRangeV2?.minVariantPrice?.currencyCode || 'USD';

  const pageCanonicalUrl = options?.canonicalUrl;
  if (!pageCanonicalUrl) {
    return buildLegacyProduct(shopifyProduct, selectedVariant, priceCurrency);
  }

  if (variants.length > 1) {
    return {
      '@context': 'https://schema.org',
      '@graph': [
        buildProductGroup(
          shopifyProduct,
          variants,
          selectedVariant,
          priceCurrency,
          activeSku,
          options,
          pageCanonicalUrl
        ),
      ],
    };
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      buildGraphProduct(
        shopifyProduct,
        selectedVariant,
        priceCurrency,
        pageCanonicalUrl,
        options
      ),
    ],
  };
};

export default getProductSchema;
